import React, { useMemo, useState, useEffect, useRef } from 'react';
import Map, { Marker, useControl } from 'react-map-gl';
import { useAnalytics } from '@/hooks/supabase/useAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MapPin } from 'lucide-react';

const MAPBOX_TOKEN = 'pk.eyJ1IjoidmFtbWFuIiwiYSI6ImNtOTNsNW05bzBvbzMyd29qb2g1a2NkOGMifQ.3pdZdM6_IVUetJEGw9uzoQ';

interface TokenHotspotPoint {
  city: string | null;
  province: string | null;
  country: string | null;
  total_tokens: number;
  latitude?: number | null;
  longitude?: number | null;
}

interface TrafficPoint {
  city: string | null;
  province: string | null;
  country: string | null;
  event_count: number;
  latitude?: number | null;
  longitude?: number | null;
}

// Custom legend control component
const LegendControl = () => {
  const { current: map } = useControl(() => ({
    onAdd() {
      const legend = document.createElement('div');
      legend.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
      legend.style.padding = '10px';
      legend.style.backgroundColor = 'rgba(50, 50, 50, 0.9)'; // Darker background
      legend.style.borderRadius = '4px';
      legend.style.fontSize = '12px';
      legend.style.minWidth = '150px';
      legend.style.color = 'white'; // White text for contrast
      legend.innerHTML = `
        <div style="font-weight: bold; margin-bottom: 8px;">Legend</div>
        <div style="display: flex; align-items: center; margin-bottom: 4px;">
          <div style="width: 12px; height: 12px; background-color: rgba(107, 114, 128, 0.5); border: 2px solid #6b7280; border-radius: 50%; margin-right: 8px;"></div>
          <span>Hot Traffic</span>
        </div>
        <div style="display: flex; align-items: center;">
          <div style="width: 16px; height: 16px; background-color: rgba(234, 56, 76, 0.5); border: 2px solid #ea384c; border-radius: 50%; margin-right: 8px;"></div>
          <span>Token Usage Hotspots</span>
        </div>
      `;
      return legend;
    },
    onRemove() {},
  }), { position: 'bottom-left' });

  return null;
};

const UsageMap = () => {
  const { data, loading, error } = useAnalytics();
  const [geocodingLoading, setGeocodingLoading] = useState(true);

  useEffect(() => {
    if (!loading) {
      setGeocodingLoading(false);
    }
  }, [loading]);

  const tokenHotspots = useMemo(() => {
    if (!data?.token_usage_by_location) return [];
    
    return data.token_usage_by_location.filter(loc => {
      return loc.latitude !== null && loc.longitude !== null;
    }) as TokenHotspotPoint[];
  }, [data]);

  const trafficData = useMemo(() => {
    if (!data?.traffic_by_location) return [];
    
    return data.traffic_by_location.filter(loc => {
      return loc.latitude !== null && loc.longitude !== null;
    }) as TrafficPoint[];
  }, [data]);

  if (loading || geocodingLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Usage Hotspots (Last 30 Days)
          </CardTitle>
          <CardDescription>
            Loading map and location data...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[500px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return <p className="text-destructive">Error loading map data: {error.message}</p>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Usage Hotspots & Traffic (Last 30 Days)
        </CardTitle>
        <CardDescription>
          A map showing where your users are. Gray circles represent general traffic (including Google Analytics page views), while darker and larger primary-colored circles indicate higher token usage.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div style={{ height: '500px', width: '100%', borderRadius: '0.5rem', overflow: 'hidden' }}>
          <Map
            initialViewState={{
              longitude: -95,
              latitude: 55,
              zoom: 3,
            }}
            mapStyle="mapbox://styles/mapbox/dark-v11"
            mapboxAccessToken={MAPBOX_TOKEN}
          >
            <LegendControl />
            {trafficData.map((loc, index) => (
              <Marker key={`traffic-${index}`} longitude={loc.longitude!} latitude={loc.latitude!}>
                <div
                  className="bg-gray-400/50 rounded-full border-2 border-gray-500"
                  style={{
                    width: `${Math.max(8, Math.log(loc.event_count || 1) * 2.5)}px`,
                    height: `${Math.max(8, Math.log(loc.event_count || 1) * 2.5)}px`,
                    opacity: Math.min(1, 0.4 + (loc.event_count || 0) / 500),
                    zIndex: 1,
                  }}
                  title={`Traffic: ${loc.city || 'Unknown City'}, ${loc.province || 'N/A'}\nEvents: ${loc.event_count.toLocaleString()}`}
                />
              </Marker>
            ))}
            {tokenHotspots.map((loc, index) => (
              <Marker key={`token-${index}`} longitude={loc.longitude!} latitude={loc.latitude!}>
                <div
                  className="bg-primary/50 rounded-full flex items-center justify-center border-2 border-primary"
                  style={{
                    width: `${Math.max(10, Math.log(loc.total_tokens || 1) * 3)}px`,
                    height: `${Math.max(10, Math.log(loc.total_tokens || 1) * 3)}px`,
                    opacity: Math.min(1, 0.5 + (loc.total_tokens || 0) / 100000),
                    zIndex: 2,
                  }}
                  title={`Token Usage: ${loc.city || 'Unknown City'}, ${loc.province || 'N/A'}\nTokens: ${loc.total_tokens.toLocaleString()}`}
                />
              </Marker>
            ))}
          </Map>
        </div>
      </CardContent>
    </Card>
  );
};

export default UsageMap;